// =========================================================
// Components/PageMeta.js
//
// One-line SEO + tab-title manager for any page.
//
//   <PageMeta title="العملاء" />                       app page: tab title only
//   <PageMeta title="تسجيل دخول"                       public page: the works
//             description="ادخل لحسابك في الريّس"
//             noIndex />
//
// WHAT IT GUARANTEES
//   - a unique <title> per route, which is the single strongest on-page
//     signal Google has for what a page is about;
//   - a canonical URL, so the same screen reached through two paths is not
//     read as two competing pages;
//   - absolute og:image and og:url — social crawlers and Google resolve
//     these as given, and a relative "/logo512.png" resolves to nothing;
//   - `noindex` on everything behind a login, so a leaked link cannot put an
//     empty dashboard shell into the index under the site's own name.
//
// Authenticated pages only need a title. Public pages should pass a
// description too: without one Google writes its own from whatever text it
// finds, which for an SPA shell is often nothing at all.
// =========================================================
import { Helmet } from "react-helmet-async";

const SITE = "الريّس";

// The origin this page is actually being served from. Canonicals must be
// absolute, and hard-coding the production domain would make every canonical
// on a staging build point at production — telling Google to index the wrong
// host. Reading it from the browser keeps each deployment honest about itself.
const origin = () =>
  (typeof window !== "undefined" && window.location?.origin) || "https://elrayes-ac.com";

const absolute = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `${origin()}${url.startsWith("/") ? "" : "/"}${url}`;
};

const PageMeta = ({
    title,
    description,
    keywords,
    // A 1200x630 card rather than the square logo: square images get
    // letterboxed or cropped to a thumbnail in a timeline.
    ogImage = "/og-cover.png",
    canonical,
    noIndex = false,
    // "noindex, follow" rather than "noindex, nofollow". A 404 should still
    // pass link equity through the links it shows; a dashboard should not.
    follow = false,
    // "website" for pages, "article" for anything dated and authored.
    ogType = "website",
    // Extra structured data for this page, as a plain object.
    jsonLd,
}) => {
    // "الريّس — نظام تشغيل شركات التكييف · الريّس" is what unconditional
    // suffixing produced on the home page, where the CMS title already
    // carries the brand. Suffix only when the brand is not already in it.
    const fullTitle = !title
      ? `${SITE} — نظام تشغيل شركات التكييف في مصر`
      : (title.includes(SITE) ? title : `${title} · ${SITE}`);
    // A noindex page gets no canonical at all.
    //
    // react-helmet rewrites the head AFTER the server has written it, so a
    // self-referencing canonical here would put one back on a 404 — which is
    // the strongest signal available that the page is real and should be
    // indexed. It also has nothing to canonicalise: a page that may not be
    // in the index cannot consolidate signals into itself.
    const url = noIndex
      ? undefined
      : (canonical
          ? absolute(canonical)
          : (typeof window !== "undefined" ? window.location.href.split("?")[0].split("#")[0] : undefined));
    const image = absolute(ogImage);

    return (
        <Helmet>
            <title>{fullTitle}</title>
            {description && <meta name="description" content={description} />}
            {keywords && <meta name="keywords" content={keywords} />}
            {url && <link rel="canonical" href={url} />}

            {/* max-image-preview / max-snippet let Google show a large image
                and a full snippet; without them it may choose a thumbnail. */}
            <meta
              name="robots"
              content={
                noIndex
                  ? (follow ? "noindex, follow" : "noindex, nofollow")
                  : "index, follow, max-image-preview:large, max-snippet:-1"
              }
            />

            {/* Open Graph */}
            <meta property="og:site_name" content={SITE} />
            <meta property="og:title" content={fullTitle} />
            {description && <meta property="og:description" content={description} />}
            <meta property="og:image" content={image} />
            <meta property="og:type" content={ogType} />
            <meta property="og:locale" content="ar_EG" />
            {url && <meta property="og:url" content={url} />}

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            {description && <meta name="twitter:description" content={description} />}
            <meta name="twitter:image" content={image} />

            {jsonLd && (
              <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            )}
        </Helmet>
    );
};

export default PageMeta;
